import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { CAREERS_COPY } from '../../../constants/careersTranslations';

export function CareersCultureSection({ lang = 'en', copy }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const t = copy?.culture ?? CAREERS_COPY[lang]?.culture ?? CAREERS_COPY.en.culture;

  return (
    <section ref={ref} className="section-rhythm bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] aspect-[4/3] bg-gray-200"
          >
            <Image
              src="/careers/coffee-mugs.png"
              alt={lang === 'fi' ? 'Identio-kahvikuppi pöydällä' : 'Identio coffee cup on a table with two people sitting in the background'}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="heading-2 mb-6 text-gray-900">
              {t.title1}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {t.body1}
            </p>
            {t.linkHref && t.linkText && (
              <Link
                href={t.linkHref}
                className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
              >
                {t.linkText}
                <ArrowRight className="w-4 h-4 shrink-0" />
              </Link>
            )}
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <h2 className="heading-2 mb-6 text-gray-900">
              {t.title2}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t.body2}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] aspect-[4/3] bg-gray-200"
          >
            <Image
              src="/careers/office-dice.png"
              alt={lang === 'fi' ? 'Toimistokulttuuri' : 'Office culture—dice, kombucha, and collaborative work'}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
